
import {Component, Vue} from "vue-property-decorator";
import {ProgramPaths} from "@/enum/ProgramPaths";
import sorguOneriService, {SorguOlayRow, SorguOneriRow} from "@/services/sorguOneriService";
import {dosyaRefCoz, koprulenebilirMi} from "@/services/sorguOneriKopru";

/**
 * Açık haciz önerilerini listeleyen panel (F2-İP5, DEV-2952).
 *
 * Yerleşim Ö1 kararı: bu panel İşlemler sayfasında durur, üst çubuktaki
 * `SorguOneriInfo` yalnız sayısal rozet gösterip buraya yönlendirir. İkisi de
 * aynı `sorguOneri` store modülünden beslenir.
 */
@Component({})
export default class SorguOneriList extends Vue {
  /** "Neden?" bölümü açık olan önerinin kimliği. */
  acikNedenId: number | null = null;
  olaylar: Array<SorguOlayRow> = [];
  olayYukleniyor = false;
  /** Olay gunlugu istegi hata verdi mi — "kayit yok" ile karistirilmamali. */
  olayHatasi = false;
  /** Durum geçişi süren öneri — çift tıklamayı engeller. */
  islemdekiId: number | null = null;

  get oneriler(): Array<SorguOneriRow> {
    return this.$store.getters["sorguOneri/acikOneriler"];
  }

  get yukleniyor(): boolean {
    return this.$store.getters["sorguOneri/oneriYukleniyor"];
  }

  get hata(): string | null {
    return this.$store.getters["sorguOneri/oneriHatasi"];
  }

  mounted() {
    this.$store.dispatch("sorguOneri/yukle");
  }

  yenile() {
    this.acikNedenId = null;
    this.$store.dispatch("sorguOneri/yukle");
  }

  koprulenebilir(oneri: SorguOneriRow): boolean {
    return koprulenebilirMi(oneri);
  }

  dosyaNoMetni(oneri: SorguOneriRow): string {
    const kimlik = dosyaRefCoz(oneri.dosya_ref);
    return kimlik ? kimlik.dosyaNo : oneri.dosya_ref;
  }

  olayTipiMetni(tip: string): string {
    if (tip === "eklendi") return "Eklendi";
    if (tip === "cikarildi") return "Çıkarıldı";
    return "Değişti";
  }

  /**
   * Ö3 kararı: olay günlüğü yalnız kartın "neden" ayrıntısı olarak görünür,
   * ayrı bir günlük ekranı yok. Sorgu, öneriyi doğuran alan/varlık ile daraltılır.
   */
  async nedenGoster(oneri: SorguOneriRow) {
    if (this.acikNedenId === oneri.id) {
      this.acikNedenId = null;
      return;
    }
    this.acikNedenId = oneri.id;
    this.olaylar = [];
    this.olayHatasi = false;
    this.olayYukleniyor = true;
    try {
      const olaylar = await sorguOneriService.olaylar(oneri.dosya_ref, {
        alan: oneri.sihirbaz_param?.alan,
        borclu_ref: oneri.borclu_ref,
        entity_key: oneri.entity_key ?? undefined,
        limit: 20
      });
      // kod inceleme: cevap gelene kadar kullanıcı başka bir öneriyi açmış olabilir;
      // artık açık olan öneri değiştiyse eski cevabı uygulama (yarış durumu koruması)
      if (this.acikNedenId !== oneri.id) return;
      this.olaylar = olaylar;
    } catch (e) {
      if (this.acikNedenId !== oneri.id) return;
      this.olaylar = [];
      this.olayHatasi = true;
    } finally {
      if (this.acikNedenId === oneri.id) {
        this.olayYukleniyor = false;
      }
    }
  }

  async yokSay(oneri: SorguOneriRow) {
    this.islemdekiId = oneri.id;
    try {
      await this.$store.dispatch("sorguOneri/yokSay", oneri.id);
      if (this.acikNedenId === oneri.id) this.acikNedenId = null;
    } catch (e) {
      // Hata uyarısını axios yanıt interceptor'ı gösteriyor; kart listede kalır.
    } finally {
      this.islemdekiId = null;
    }
  }

  /**
   * Köprü (F2-İP6): sihirbaz sayfasına önerinin kimliğiyle gidilir.
   *
   * Burada durum `goruldu` yapılır — AÇIK bir durumdur, kural yeniden
   * tetiklenmesini engellemez. `gorev_olusturuldu` geçişini sihirbaz sayfası,
   * görevler gerçekten kuyruğa alındığında yapar; kullanıcı sihirbazı yarıda
   * bırakırsa öneri açık kalmalıdır.
   */
  async talepHazirla(oneri: SorguOneriRow) {
    this.$store.dispatch("sorguOneri/koprulenenAyarla", oneri);
    this.$store.dispatch("sorguOneri/durumGuncelle", {id: oneri.id, durum: "goruldu"}).catch(() => {
      // Durum yazılamasa da köprü açılmalı; kart bir sonraki yüklemede yine gelir.
    });
    await this.$router.push({
      path: ProgramPaths.eicrapro + "/toplu-haciz-talebi-gonder",
      query: {oneri: String(oneri.id), dosya_ref: oneri.dosya_ref}
    });
  }
}
